/**
 * Turbo Chat Integration Service
 * Replaces Charla — uses the Turbo Chat widget embedded via widget.js
 *
 * The widget.js script (loaded in index.html) exposes window.TurboChat
 * with open(), close(), toggle() methods.
 *
 * We add data-hide-button="true" so the floating bubble is hidden.
 * Support button in the sidebar calls open() directly.
 */

const CHAT_URL = 'https://chat.turbo.net.au';
const SITE_ID = '90eea063-bba4-4b3d-b9ec-9c87b3cef5b6';

class TurboChatService {
  constructor() {
    this.initialized = false;
    this.userData = null;
    this.iframePatched = false;
    this._unreadListener = null;
  }

  /**
   * Initialize with logged-in user data.
   * Patches the widget iframe src to include name & email query params
   * so the pre-chat form can be pre-filled or skipped.
   */
  initializeWithUser(user) {
    if (!user) return;
    this.userData = user;
    this.initialized = true;

    // Patch iframe src with user data once the widget creates it
    this._patchIframe();

    // Listen for new messages and auto-open chat
    this._listenForMessages();
  }

  /**
   * Patch the Turbo Chat iframe to include visitor name/email in the URL.
   * The widget.js creates the iframe after DOMContentLoaded, so we observe.
   */
  _patchIframe() {
    if (this.iframePatched) return;

    const tryPatch = () => {
      const container = document.getElementById('turbo-chat-container');
      if (!container) return false;

      const iframe = container.querySelector('iframe');
      if (!iframe || !this.userData) return false;

      const url = new URL(iframe.src);
      const name = this.userData.full_name || this.userData.name || '';
      const email = this.userData.email || '';
      if (name) url.searchParams.set('visitorName', name);
      if (email) url.searchParams.set('visitorEmail', email);
      iframe.src = url.toString();
      this.iframePatched = true;
      return true;
    };

    // Try immediately
    if (tryPatch()) return;

    // Observe for when widget.js injects the iframe
    const observer = new MutationObserver(() => {
      if (tryPatch()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Safety: stop observing after 15s
    setTimeout(() => observer.disconnect(), 15000);
  }

  /**
   * Listen for incoming admin messages and auto-open the chat window.
   * The widget.js dispatches 'turbo-chat:unread-update' custom events on document,
   * and also posts 'turbo-chat:unread' / 'turbo-chat:proactive-open' via postMessage.
   */
  _listenForMessages() {
    // Remove previous listener if any
    if (this._unreadListener) {
      window.removeEventListener('message', this._unreadListener);
    }

    this._unreadListener = (event) => {
      if (!event.data || typeof event.data !== 'object') return;
      const { type } = event.data;
      if (type === 'turbo-chat:unread' || type === 'turbo-chat:proactive-open') {
        if (window.TurboChat) {
          window.TurboChat.open();
        }
      }
    };

    window.addEventListener('message', this._unreadListener);
  }

  /**
   * Open the chat window (called by Support button).
   */
  open() {
    if (window.TurboChat) {
      window.TurboChat.open();
    } else {
      console.warn('[TurboChat] Widget not loaded yet');
    }
  }

  /**
   * Close the chat window.
   */
  close() {
    if (window.TurboChat) {
      window.TurboChat.close();
    }
  }

  /**
   * Toggle chat open/closed.
   */
  toggle() {
    if (window.TurboChat) {
      window.TurboChat.toggle();
    }
  }

  /**
   * Reset on logout.
   */
  reset() {
    this.initialized = false;
    this.userData = null;
    this.iframePatched = false;
    if (this._unreadListener) {
      window.removeEventListener('message', this._unreadListener);
      this._unreadListener = null;
    }
  }
}

const turboChatService = new TurboChatService();
export default turboChatService;
